import React, { useCallback, useImperativeHandle, forwardRef, useState, useRef, useEffect } from "react";
import ReactPlayer from "react-player";
import "./UserVideoModal.css";
import { useVideoPlayer } from "../../../hooks/UserHooks/MyCoursesHook/useVideoPlayer";
import { useSelector } from "react-redux";

const formatTime = (secs) => {
  if (!secs || isNaN(secs)) return "0:00";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const SPEED_OPTIONS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const UserVideoModal = forwardRef((
  // { isOpen, fileName, resourceType, onClose, contentId, courseId, handleClose: onHandleClose, isAcknowledged },
  { isOpen, fileName, resourceType, onClose, contentId, courseId, handleClose: onHandleClose, onSuccess, isContentCompleted, isAcknowledged },
  ref
) => {
  const userRole = useSelector((state) => state.auth?.roles?.[0]);
  const currentPortal = useSelector((state) => state.auth?.currentPortal);
  const isEmployee = userRole === "EMPLOYEE" || (userRole === "ADMIN_SUPPORT" && currentPortal === "user");

  const {
    playerRef,
    videoUrl,
    isEnded,
    videoLoading,
    totalDuration,
    isPlaying,
    currentPosition,
    played,
    volume,
    muted,
    showControls,
    handleReady,
    playerReady,
    handleProgress,
    handleDuration,
    handleVideoEnd,
    handlePause,
    handlePlay,
    handleSeekChange,
    handleMouseMove,
    togglePlay,
    toggleMute,
    handleVolumeChange,
    handleFullscreen,
    saveProgress,
    lastWatchedPosition
  // } = useVideoPlayer({ isOpen, fileName, resourceType, contentId, courseId, isAcknowledged });
  } = useVideoPlayer({ isOpen, fileName, resourceType, contentId, courseId, onSuccess, isAcknowledged });

  const [playbackRate, setPlaybackRate] = useState(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const speedGroupRef = useRef(null);

  useEffect(() => {
    let timer;
    if (isEnded && isContentCompleted) {
      setCountdown(5);
      timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            if (typeof onSuccess === "function") {
              onSuccess();
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isEnded, isContentCompleted, onSuccess]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (speedGroupRef.current && !speedGroupRef.current.contains(e.target)) {
        setShowSpeedMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSpeedSelect = useCallback((rate) => {
    setPlaybackRate(rate);
    setShowSpeedMenu(false);
  }, []);

  useImperativeHandle(ref, () => ({
    saveProgress,
    handleClose: async () => { await saveProgress(); onClose?.(); },
  }));

  const handleClose = useCallback(async () => {
    await saveProgress();
    if (onHandleClose) onHandleClose();
    else onClose?.();
  }, [saveProgress, onHandleClose, onClose]);

  const handleCloseAdmin = useCallback(() => {
    if (onHandleClose) onHandleClose();
    else onClose?.();
  }, [onHandleClose, onClose]);

  const skipBackward = useCallback(() => {
    if (playerRef?.current && totalDuration) {
      playerRef.current.seekTo(Math.max(0, currentPosition - 10), "seconds");
    }
  }, [playerRef, currentPosition, totalDuration]);

  const skipForward = useCallback(() => {
    if (!playerRef?.current || !totalDuration) return;
    if (isEmployee) {
      const cap = lastWatchedPosition ?? currentPosition;
      const newTime = Math.min(cap, currentPosition + 10);
      playerRef.current.seekTo(newTime, "seconds");
    } else {
      playerRef.current.seekTo(Math.min(totalDuration, currentPosition + 10), "seconds");
    }
  }, [playerRef, currentPosition, totalDuration, isEmployee, lastWatchedPosition]);

  const handleSeekChangeGuarded = useCallback((val) => {
    if (isEmployee) {
      const targetSeconds = val * totalDuration;
      const cap = lastWatchedPosition ?? currentPosition;
      if (targetSeconds > cap) return;
    }
    handleSeekChange(val);
  }, [isEmployee, totalDuration, lastWatchedPosition, currentPosition, handleSeekChange]);

  if (!isOpen) return null;

  const progressPercent = totalDuration ? (currentPosition / totalDuration) * 100 : 0;
  const watchedPercent = totalDuration && isEmployee
    ? ((lastWatchedPosition ?? currentPosition) / totalDuration) * 100
    : null;

  return (
    <div
      className="video-wrapper"
      onMouseMove={handleMouseMove}
      style={{ cursor: showControls ? "default" : "none" }}
    >
      <button
        className="video-close-btn"
        onClick={isEmployee ? handleClose : handleCloseAdmin}
        aria-label="Close video"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"/>
          <line x1="6" y1="6" x2="18" y2="18"/>
        </svg>
      </button>

      {!playerReady && (
        <div className="video-loading">
          <div className="video-spinner" />
        </div>
      )}

      <div className="video-player-area" onClick={togglePlay}>
        <ReactPlayer
          key={videoUrl || "video"}
          ref={playerRef}
          url={videoUrl}
          controls={false}
          playing={isPlaying}
          volume={volume}
          muted={muted}
          playbackRate={playbackRate}
          width="100%"
          height="100%"
          onReady={handleReady}
          onProgress={handleProgress}
          onDuration={handleDuration}
          onEnded={handleVideoEnd}
          onPause={handlePause}
          onPlay={handlePlay}
          onError={(e) => console.error("Player error:", e)}
          progressInterval={500}
          className="video-player"
          style={{ pointerEvents: "none" }}
        />

        {/* --- End Screen Overlay --- */}
        {isEnded && (
          <div 
            className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/80 text-white p-6 text-center animate-fadeIn pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {isContentCompleted ? (
              /* Case 1: Already Completed -> 5s Countdown to Next Content */
              <>
                <div className="w-16 h-16 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center mb-4 border border-blue-500/40 shadow-lg font-bold text-2xl">
                  {countdown}
                </div>

                <h3 className="text-xl font-semibold mb-2 text-white">Next content in {countdown}s</h3>
                <p className="text-gray-300 text-sm max-w-sm mb-6 leading-relaxed">
                  You have already completed this lesson. Transitioning to the next module automatically.
                </p>

                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      if (typeof onSuccess === "function") onSuccess();
                    }}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#910E11] hover:bg-[#700b0d] text-white rounded-lg text-sm font-medium transition-colors shadow-md"
                  >
                    Next Content
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </button>

                  <button
                    onClick={togglePlay}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors border border-white/20"
                  >
                    Replay
                  </button>
                </div>
              </>
            ) : (
              /* Case 2: Not Yet Completed -> Prompt user to click "Mark as Done" */
              <>
                <div className="w-16 h-16 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center mb-4 border border-emerald-500/40 shadow-lg">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>

                <h3 className="text-xl font-semibold mb-2 text-white">Video Finished!</h3>
                <p className="text-gray-300 text-sm max-w-sm mb-6 leading-relaxed">
                  Great job! Click the <strong className="text-white font-medium">"Mark as Done"</strong> button at the top to complete this module and move to the next item.
                </p>

                <button
                  onClick={togglePlay}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-medium transition-colors border border-white/20"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="5,3 19,12 5,21" />
                  </svg>
                  Replay Video
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <div className={`video-controls ${showControls ? "video-controls--visible" : ""}`}>
        <div className="video-progress-wrapper">
          <div className="video-progress-bg">
            {watchedPercent !== null && (
              <div className="video-progress-watched" style={{ width: `${watchedPercent}%` }} />
            )}
            <div className="video-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
          <input
            type="range"
            className="video-seek"
            min={0} max={1} step={0.001}
            value={played}
            onChange={(e) => handleSeekChangeGuarded(parseFloat(e.target.value))}
            style={isEmployee ? { cursor: "pointer" } : undefined}
          />
        </div>

        <div className="video-controls-row">
          <div className="video-controls-left">
            <button className="video-btn" onClick={skipBackward} aria-label="Skip backward 10s">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
                <text x="12" y="16" textAnchor="middle" fontSize="7" fill="white" stroke="none" fontWeight="600">10</text>
              </svg>
            </button>

            <button className="video-btn video-btn-play" onClick={togglePlay} aria-label={isPlaying ? "Pause" : "Play"}>
              {isPlaying ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="4" width="4" height="16" rx="1"/>
                  <rect x="14" y="4" width="4" height="16" rx="1"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5,3 19,12 5,21"/>
                </svg>
              )}
            </button>
            <button
              className="video-btn"
              onClick={skipForward}
              aria-label="Skip forward 10s"
              style={
                isEmployee && currentPosition >= (lastWatchedPosition ?? currentPosition)
                  ? { opacity: 0.4, cursor: "not-allowed" }
                  : undefined
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12a9 9 0 1 1-9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
                <text x="12" y="16" textAnchor="middle" fontSize="7" fill="white" stroke="none" fontWeight="600">10</text>
              </svg>
            </button>

            <span className="video-time">
              {formatTime(currentPosition)} / {formatTime(totalDuration)}
            </span>
          </div>

          <div className="video-controls-right">
            <div className="video-volume-group">
              <button className="video-btn" onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"}>
                {muted || volume === 0 ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"/>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"/>
                  </svg>
                )}
              </button>
              <div className="video-volume-bar-wrapper">
                <div className="video-volume-bg">
                  <div className="video-volume-fill" style={{ width: `${(muted ? 0 : volume) * 100}%` }} />
                </div>
                <input
                  type="range"
                  className="video-volume"
                  min={0} max={1} step={0.01}
                  value={muted ? 0 : volume}
                  onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                />
              </div>
            </div>

            <div className="video-speed-group" ref={speedGroupRef}>
              <button
                className="video-btn video-speed-btn"
                onClick={() => setShowSpeedMenu((p) => !p)}
                aria-label="Playback speed"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                <span className="video-speed-label">
                  {playbackRate === 1 ? "1×" : `${playbackRate}×`}
                </span>
              </button>

              {showSpeedMenu && (
                <div className="video-speed-menu">
                  <div className="video-speed-menu-header">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                    </svg>
                    Playback speed
                  </div>
                  {SPEED_OPTIONS.map((rate) => {
                    const isActive = playbackRate === rate;
                    return (
                      <button
                        key={rate}
                        className={`video-speed-option ${isActive ? "video-speed-option--active" : ""}`}
                        onClick={() => handleSpeedSelect(rate)}
                      >
                        <span className="video-speed-check-slot">
                          {isActive && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12"/>
                            </svg>
                          )}
                        </span>
                        <span>{rate === 1 ? "Normal" : `${rate}×`}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button className="video-btn" onClick={handleFullscreen} aria-label="Fullscreen">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default UserVideoModal;